import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Product, CreateProductInput, UpdateProductInput } from "../types";

const PRODUCT_FIELDS = `
  id
  organizationId
  sku
  productCode
  barcode
  qrCode
  productName
  shortName
  description
  categoryId
  subCategoryId
  brandId
  supplierId
  manufacturer
  unit
  purchasePrice
  sellingPrice
  currency
  taxPercentage
  taxCode
  discountPercentage
  inventoryTracking
  totalStock
  reorderLevel
  maximumStockLevel
  allowBackOrder
  status
  dimensions {
    length
    width
    height
    weight
    dimensionUnit
    weightUnit
  }
  images {
    id
    key
    url
    thumbnailUrl
    fileName
    mimeType
    fileSize
    isPrimary
    sortOrder
    uploadedAt
    uploadedBy
  }
  tags
  notes
  batchTracking
  serialTracking
  expiryTracking
  countryOfOrigin
  hsnCode
  upc
  ean
  isFeatured
  isTaxable
  createdBy
  updatedBy
  createdAt
  updatedAt
`;

export const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // `organizationId` is optional — the resolver falls back to the caller's
    // org from the access token when omitted.
    getProducts: builder.query<Product[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Products($organizationId: String) {
              products(organizationId: $organizationId) {
                ${PRODUCT_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Product[]>(res, "products"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "Product" as const, id: p.id })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),

    getProduct: builder.query<Product, string>({
      query: (id) => ({
        body: {
          query: `
            query Product($id: String!) {
              product(id: $id) {
                ${PRODUCT_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Product>(res, "product"),

      providesTags: (_result, _err, id) => [{ type: "Product", id }],
    }),

    createProduct: builder.mutation<Product, CreateProductInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateProduct($input: CreateProductInput!) {
              createProduct(input: $input) {
                ${PRODUCT_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Product>(res, "createProduct"),

      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),

    updateProduct: builder.mutation<
      Product,
      { id: string; input: UpdateProductInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateProduct($id: String!, $input: UpdateProductInput!) {
              updateProduct(id: $id, input: $input) {
                ${PRODUCT_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Product>(res, "updateProduct"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Product", id },
        { type: "Product", id: "LIST" },
      ],
    }),

    deleteProduct: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteProduct($id: String!) {
              deleteProduct(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteProduct"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Product", id },
        { type: "Product", id: "LIST" },
      ],
    }),

    // Apply one set of changes to many products at once (bulk edit).
    bulkUpdateProducts: builder.mutation<
      Product[],
      { ids: string[]; input: UpdateProductInput }
    >({
      query: ({ ids, input }) => ({
        body: {
          query: `
            mutation BulkUpdateProducts(
              $ids: [String!]!
              $input: UpdateProductInput!
            ) {
              bulkUpdateProducts(ids: $ids, input: $input) {
                ${PRODUCT_FIELDS}
              }
            }
          `,
          variables: { ids, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<Product[]>(res, "bulkUpdateProducts"),

      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),

    // Delete many products at once. Returns the number removed.
    bulkDeleteProducts: builder.mutation<number, string[]>({
      query: (ids) => ({
        body: {
          query: `
            mutation BulkDeleteProducts($ids: [String!]!) {
              bulkDeleteProducts(ids: $ids)
            }
          `,
          variables: { ids },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<number>(res, "bulkDeleteProducts"),

      invalidatesTags: [{ type: "Product", id: "LIST" }],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductQuery,
  useLazyGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkUpdateProductsMutation,
  useBulkDeleteProductsMutation,
} = productApi;
